import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getJson } from "@shared/http";
import { PlacesAutocompleteSchema, PlacesDetailsSchema } from "@shared/api-schemas";

interface RegionAutocompleteProps {
  value?: string;
  onChange: (value: string, details?: RegionDetails) => void;
  onDetailsChange?: (details: RegionDetails) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  onErrorChange?: (hasError: boolean) => void;
}

export interface RegionDetails {
  suburb: string;
  city: string;
  state: string;
  postcode: string;
  fullAddress: string;
  formatted: string;
}

declare global {
  interface Window {
    google: any;
  }
}

export function RegionAutocomplete({
  value = "",
  onChange,
  onDetailsChange,
  label = "지역 또는 우편번호",
  placeholder = "지역명이나 우편번호를 입력하세요 (예: Epping, 2121)",
  required = false,
  className = "",
  onErrorChange,
}: RegionAutocompleteProps) {
  // Ensure value is always a string
  const safeValue = value ?? "";
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string>("");

  // Load Google Maps script using centralized loader
  useEffect(() => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      setScriptLoaded(true);
      setIsLoading(false);
      return;
    }

    // Use centralized Google Maps loader
    import("@/lib/googleMapsLoader").then(({ loadGoogleMaps }) => {
      loadGoogleMaps()
        .then(() => {
          setScriptLoaded(true);
          setIsLoading(false);
          if (onErrorChange) onErrorChange(false);
        })
        .catch((error) => {
          // Silently handle missing API key - autocomplete feature will be disabled
          console.warn("Google Maps API key not configured - autocomplete disabled", error);
          setIsLoading(false);
          if (onErrorChange) onErrorChange(true);
        });
    }).catch((error) => {
      console.error("Failed to import googleMapsLoader:", error);
      setIsLoading(false);
      if (onErrorChange) onErrorChange(true);
    });
  }, [onErrorChange]);

  // Handle input change and fetch predictions
  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value ?? "";
    onChange(newValue);

    if (newValue.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // Use (regions) type for suburb/city/postal_code autocomplete
      const response = await fetch(`/api/places/autocomplete?input=${encodeURIComponent(newValue)}&types=(regions)`);

      if (!response.ok) {
        console.error("❌ Autocomplete request failed:", response.status, response.statusText);
        return;
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error("❌ Response is not JSON. Content-Type:", contentType);
        return;
      }

      const data = await getJson(
        `/api/places/autocomplete?input=${encodeURIComponent(newValue)}&types=(regions)`,
        {},
        PlacesAutocompleteSchema
      );

      console.log("📍 Region autocomplete response:", data);

      if (data.status === "REQUEST_DENIED") {
        console.error("❌ Google API Request Denied:", data.error_message);
        const errorMsg = data.error_message || "Google API 요청이 거부되었습니다";
        setError(errorMsg);
        if (onErrorChange) onErrorChange(true);
        return;
      }

      // Clear error if request succeeds
      if (onErrorChange && error) {
        onErrorChange(false);
        setError("");
      }

      if (data.predictions && data.predictions.length > 0) {
        console.log("✅ Found", data.predictions.length, "region suggestions");
        setSuggestions(data.predictions);
        setShowSuggestions(true);
      } else {
        console.log("ℹ️ No region suggestions found");
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("❌ Error fetching region suggestions:", error);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = async (placeId: string, description: string) => {
    try {
      const data = await getJson(
        `/api/places/details?placeId=${encodeURIComponent(placeId)}`,
        {},
        PlacesDetailsSchema
      );

      if (data.result) {
        const details = extractRegionDetails(data.result, description);
        onChange(details.formatted, details);

        if (onDetailsChange) {
          onDetailsChange(details);
        }
      } else {
        // Fallback
        onChange(description);
      }

      setSuggestions([]);
      setShowSuggestions(false);
    } catch (error) {
      console.error("Error fetching place details:", error);
      // Fallback
      onChange(description);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const extractRegionDetails = (place: any, fallbackDescription: string): RegionDetails => {
    const components = place.address_components || [];
    let suburb = "";
    let city = "";
    let state = "";
    let postcode = "";

    components.forEach((component: any) => {
      const types = component.types;

      // Suburb/locality
      if (types.includes("locality")) {
        suburb = component.long_name;
      }
      // City (administrative_area_level_2)
      if (types.includes("administrative_area_level_2")) {
        city = component.long_name;
      }
      // State
      if (types.includes("administrative_area_level_1")) {
        state = component.short_name;
      }
      // Postcode
      if (types.includes("postal_code")) {
        postcode = component.long_name;
      }
    });

    // Build formatted string
    const parts = [];
    if (suburb) parts.push(suburb);
    if (state) parts.push(state);
    if (postcode) parts.push(postcode);

    const formatted = parts.length > 0 ? parts.join(", ") : fallbackDescription;
    const fullAddress = place.formatted_address || fallbackDescription;

    return {
      suburb: suburb || city,
      city,
      state,
      postcode,
      fullAddress,
      formatted,
    };
  };

  // Clear input
  const handleClear = () => {
    onChange("");
    setSuggestions([]);
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!scriptLoaded && isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        {label && <Label>{label} {required && <span className="text-destructive">*</span>}</Label>}
        <div className="relative">
          <Input
            placeholder={placeholder}
            disabled
            className="pl-10"
          />
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground">자동완성 로딩 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`space-y-2 ${className}`}>
        {label && <Label>{label} {required && <span className="text-destructive">*</span>}</Label>}
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            value={safeValue}
            onChange={(e) => onChange(e.target.value ?? "")}
            placeholder={placeholder}
            className="pl-10 pr-10"
            data-testid="input-region-autocomplete"
          />
          {safeValue && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={handleClear}
              data-testid="button-clear-region"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        <p className="text-xs text-destructive">⚠️ {error} - 지역명을 직접 입력해주세요</p>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label>{label} {required && <span className="text-destructive">*</span>}</Label>}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={safeValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="pl-10 pr-10"
          data-testid="input-region-autocomplete"
          autoComplete="off"
        />

        {safeValue && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={handleClear}
            data-testid="button-clear-region"
          >
            <X className="h-3 w-3" />
          </Button>
        )}

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                className="px-4 py-2 hover-elevate cursor-pointer border-b last:border-b-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  console.log("🔍 Selected region:", suggestion.description, suggestion.place_id);
                  handleSuggestionClick(suggestion.place_id, suggestion.description);
                }}
                role="option"
                data-testid={`suggestion-${suggestion.place_id}`}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {suggestion.description}
                    </div>
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-xs text-muted-foreground">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        예: "Epping", "시드니", "2121" - 2글자 이상 입력하면 호주 지역 목록이 표시됩니다
      </p>
    </div>
  );
}
